/**
 * Accordion Panel Web Component
 *
 * A custom element that provides smooth accordion open/close animations
 * using the Web Animation API for hardware-accelerated performance.
 *
 * Usage:
 * <accordion-panel duration="300" easing="ease-out">
 *   <button slot="trigger">Question or Title</button>
 *   <div slot="content">
 *     Answer or collapsible content...
 *   </div>
 * </accordion-panel>
 *
 * Attributes:
 * - duration: Animation duration in ms (default: 300)
 * - easing: CSS easing function (default: 'ease-out')
 * - open: Boolean attribute to start open
 * - exclusive: When set, only one accordion in the same group can be open
 * - group: Group name for exclusive mode (default: uses parent element)
 *
 * Events:
 * - accordion:open - Fired when accordion opens
 * - accordion:close - Fired when accordion closes
 * - accordion:toggle - Fired on any toggle with detail { open: boolean }
 */

class AccordionPanel extends HTMLElement {
  static get observedAttributes() {
    return ['open', 'duration', 'easing'];
  }

  constructor() {
    super();

    this._isOpen = false;
    this._isAnimating = false;
    this._animation = null;

    // Bind methods
    this._handleTriggerClick = this._handleTriggerClick.bind(this);
    this._handleKeydown = this._handleKeydown.bind(this);
  }

  connectedCallback() {
    // Generate unique IDs for accessibility
    const id = this.id || `accordion-${Math.random().toString(36).slice(2, 9)}`;
    this._contentId = `${id}-content`;
    this._triggerId = `${id}-trigger`;

    // Get elements
    this._trigger = this.querySelector('[slot="trigger"]');
    this._content = this.querySelector('[slot="content"]');

    if (!this._trigger || !this._content) {
      console.warn('AccordionPanel: Missing trigger or content slot');
      return;
    }

    // Setup trigger
    this._trigger.id = this._triggerId;
    this._trigger.setAttribute('aria-expanded', 'false');
    this._trigger.setAttribute('aria-controls', this._contentId);

    // Setup content
    this._content.id = this._contentId;
    this._content.setAttribute('aria-hidden', 'true');
    this._content.style.overflow = 'hidden';
    this._content.style.height = '0';
    this._content.style.opacity = '0';

    // Event listeners
    this._trigger.addEventListener('click', this._handleTriggerClick);
    this._trigger.addEventListener('keydown', this._handleKeydown);

    // Check if should start open
    if (this.hasAttribute('open')) {
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        this._openInstant();
      });
    }
  }

  disconnectedCallback() {
    if (this._trigger) {
      this._trigger.removeEventListener('click', this._handleTriggerClick);
      this._trigger.removeEventListener('keydown', this._handleKeydown);
    }

    // Cancel any running animation
    if (this._animation) {
      this._animation.cancel();
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'open' && this._content) {
      if (newValue !== null) {
        this.open();
      } else {
        this.close();
      }
    }
  }

  // Public API
  get isOpen() {
    return this._isOpen;
  }

  get duration() {
    return parseInt(this.getAttribute('duration')) || 300;
  }

  get easing() {
    return this.getAttribute('easing') || 'cubic-bezier(0.4, 0, 0.2, 1)';
  }

  toggle() {
    if (this._isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    if (this._isOpen || this._isAnimating || !this._content) return;

    // Handle exclusive mode - close siblings
    if (this.hasAttribute('exclusive')) {
      this._closeSiblings();
    }

    this._isAnimating = true;
    this._isOpen = true;
    this.classList.add('is-animating');

    // Update ARIA
    this._trigger.setAttribute('aria-expanded', 'true');
    this._content.setAttribute('aria-hidden', 'false');

    // Measure content height
    this._content.style.height = 'auto';
    const targetHeight = this._content.scrollHeight;
    this._content.style.height = '0';

    // Force reflow
    this._content.offsetHeight;

    // Cancel any existing animation
    if (this._animation) {
      this._animation.cancel();
    }

    // Animate using Web Animation API
    this._animation = this._content.animate(
      [
        { height: '0px', opacity: 0 },
        { height: `${targetHeight}px`, opacity: 1 },
      ],
      {
        duration: this.duration,
        easing: this.easing,
        fill: 'forwards',
      }
    );

    this._animation.onfinish = () => {
      this._content.style.height = 'auto';
      this._content.style.opacity = '1';
      this._isAnimating = false;
      this.classList.remove('is-animating');
      this.classList.add('is-open');

      this.dispatchEvent(new CustomEvent('accordion:open', { bubbles: true }));
      this.dispatchEvent(
        new CustomEvent('accordion:toggle', {
          bubbles: true,
          detail: { open: true },
        })
      );
    };

    this._animation.oncancel = () => {
      this._isAnimating = false;
      this.classList.remove('is-animating');
    };
  }

  close() {
    if (!this._isOpen || this._isAnimating || !this._content) return;

    this._isAnimating = true;
    this.classList.add('is-animating');
    this.classList.remove('is-open');

    // Measure current height
    const currentHeight = this._content.scrollHeight;

    // Cancel any existing animation
    if (this._animation) {
      this._animation.cancel();
    }

    // Animate using Web Animation API
    this._animation = this._content.animate(
      [
        { height: `${currentHeight}px`, opacity: 1 },
        { height: '0px', opacity: 0 },
      ],
      {
        duration: this.duration,
        easing: this.easing,
        fill: 'forwards',
      }
    );

    this._animation.onfinish = () => {
      this._content.style.height = '0';
      this._content.style.opacity = '0';
      this._isOpen = false;
      this._isAnimating = false;
      this.classList.remove('is-animating');

      // Update ARIA
      this._trigger.setAttribute('aria-expanded', 'false');
      this._content.setAttribute('aria-hidden', 'true');

      this.dispatchEvent(new CustomEvent('accordion:close', { bubbles: true }));
      this.dispatchEvent(
        new CustomEvent('accordion:toggle', {
          bubbles: true,
          detail: { open: false },
        })
      );
    };

    this._animation.oncancel = () => {
      this._isAnimating = false;
      this.classList.remove('is-animating');
    };
  }

  // Private methods
  _openInstant() {
    if (!this._content) return;

    this._isOpen = true;
    this._content.style.height = 'auto';
    this._content.style.opacity = '1';
    this._trigger.setAttribute('aria-expanded', 'true');
    this._content.setAttribute('aria-hidden', 'false');
    this.classList.add('is-open');
  }

  _handleTriggerClick(event) {
    event.preventDefault();
    this.toggle();
  }

  _handleKeydown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.toggle();
    }
  }

  _closeSiblings() {
    const groupName = this.getAttribute('group');
    let siblings;

    if (groupName) {
      // Find by group attribute
      siblings = document.querySelectorAll(`accordion-panel[group="${groupName}"]`);
    } else {
      // Find siblings in same parent
      siblings = this.parentElement?.querySelectorAll('accordion-panel[exclusive]') || [];
    }

    siblings.forEach((sibling) => {
      if (sibling !== this && sibling.isOpen) {
        sibling.close();
      }
    });
  }
}

// Register the custom element
if (!customElements.get('accordion-panel')) {
  customElements.define('accordion-panel', AccordionPanel);
}

